import { ArrowRightIcon, CheckCircleIcon } from '@heroicons/react/20/solid';
import Button from 'components/commercetools-ui/atoms/button';
import Link from 'components/commercetools-ui/atoms/link';
import Markdown from 'components/commercetools-ui/organisms/markdown';
import Image from 'frontastic/lib/image';
import { isStringHtml } from 'helpers/utils/isStringHtml';
import type { JSX } from 'react';
import { Reference } from 'types/reference';

export interface CtaSectionProps {
  image: {
    media: {
      alt: string;
      file: string;
    };
    title: string;
  };
  eyebrow: string;
  title: string;
  titleHtag?: string;
  subtitle: string;
  body: string;
  list: Record<string, any>;
  ctaLabel: string;
  ctaLink?: Reference;
  ctaDataAnalytics?: string;
  bgColor: string;
  isButton?: boolean;
  buttonVariant?: 'primary' | 'primary-darker-red' | 'primary-burnt-orange' | 'secondary';
  imagePosition?: 'left' | 'right';
  sectionId?: string;
}

const CtaSection: React.FC<CtaSectionProps> = ({
  image,
  eyebrow,
  title,
  titleHtag,
  subtitle,
  body,
  list,
  ctaLabel,
  ctaLink,
  ctaDataAnalytics,
  bgColor,
  isButton = false,
  buttonVariant = 'primary',
  imagePosition = 'left',
  sectionId,
}) => {
  const isHtml = isStringHtml(body);
  const imageSrc = image?.media?.file;
  const imageAlt = image?.media?.alt;
  const imageTitle = image?.title;
  const TitleHtag = titleHtag ? (titleHtag as keyof JSX.IntrinsicElements) : 'h2';
  const bgColorClass = `bg-${bgColor}`;

  const getButtonClasses = () => {
    const baseClasses = 'w-full items-center lg:w-auto';
    if (buttonVariant === 'primary-darker-red') {
      return `${baseClasses} bg-padi-red-darker hover:bg-padi-red-darkest`;
    }
    if (buttonVariant === 'primary-burnt-orange') {
      return `${baseClasses} bg-padi-burnt-orange hover:bg-padi-burnt-orange-dark`;
    }
    if (buttonVariant === 'primary') {
      return `${baseClasses} bg-padi-blue`;
    }
    return baseClasses;
  };

  return (
    <div data-id="cta-section-container" className="relative isolate" id={sectionId}>
      <div data-id="cta-section-wrapper" className={`${bgColorClass} mx-auto max-w-7xl px-24 lg:px-32`}>
        <div
          data-id="cta-section-content"
          className={`mx-auto flex max-w-2xl flex-col gap-32 p-24 md:p-32 lg:mx-0 lg:max-w-none lg:flex-row lg:items-center lg:p-64 ${
            imagePosition === 'right' ? 'lg:flex-row-reverse' : ''
          }`}
        >
          <div data-id="cta-section-image-column" className="w-full flex-none lg:max-w-sm">
            <Image
              className="aspect-square w-full rounded-md"
              src={imageSrc}
              alt={imageAlt}
              title={imageTitle}
              style={{ objectFit: 'cover' }}
            />
          </div>

          <div data-id="cta-section-text-column" className="w-full flex-auto">
            <p data-id="cta-section-eyebrow" className="text-base font-semibold leading-7 text-slate-950">
              {eyebrow}
            </p>
            <TitleHtag data-id="cta-section-title" className="mt-20 text-3xl font-bold tracking-tight">
              {title}
            </TitleHtag>
            <div data-id="cta-section-subtitle" className="mt-20 text-base font-bold tracking-tight">
              {subtitle}
            </div>
            <div data-id="cta-section-body" className="prose mt-6 text-base leading-8">
              {isHtml ? <div dangerouslySetInnerHTML={{ __html: body }} /> : <Markdown markdown={body} />}
            </div>
            <ul data-id="cta-section-list" role="list" className="mt-10 space-y-16 text-base leading-7">
              {Object.values(list).map((listItem, index) => (
                <li key={index} data-id={`cta-section-list-item-${index}`} className="flex gap-x-12">
                  <CheckCircleIcon className="h-28 w-20 flex-none text-padi-blue" aria-hidden="true" />
                  {isStringHtml(listItem?.item) ? (
                    <div dangerouslySetInnerHTML={{ __html: listItem?.item }} />
                  ) : (
                    <Markdown markdown={listItem?.item} />
                  )}
                </li>
              ))}
            </ul>
            {ctaLink && (
              <div data-id="cta-section-cta-wrapper" className="mt-40 flex">
                {isButton ? (
                  <Link padi-analytics={ctaDataAnalytics} link={ctaLink}>
                    <Button size="s" className={getButtonClasses()}>
                      {ctaLabel}
                    </Button>
                  </Link>
                ) : (
                  <Link
                    padi-analytics={ctaDataAnalytics}
                    link={ctaLink}
                    className="flex text-sm font-semibold leading-6 text-padi-blue"
                  >
                    {ctaLabel}
                    <ArrowRightIcon className="h-28 w-20 flex-none text-padi-blue" aria-hidden="true" />
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CtaSection;
